import type { Project } from '@/api/client';
import { contractorDisplayName } from '@/utils/contractorDisplayName';
import type { FilterRule } from './filterRuleTypes';
import { flagStageFilterLabel, type FlagFilterColumn } from './projectFilterMigrations';
import {
  matchDate,
  matchNumber,
  matchText,
  readerOrContractorNamesMatchText,
} from './primitives';

export function projectMatchesRule(project: Project, columnId: string, rule: FilterRule): boolean {
  if (rule.type === 'readerTagsFacet') return true;

  switch (columnId) {
    case 'name':
      return matchText(project.name, rule as Extract<FilterRule, { type: 'text' }>);
    case 'author': {
      const name = project.authors?.[0]?.name ?? null;
      return matchText(name, rule as Extract<FilterRule, { type: 'text' }>);
    }
    case 'theme':
      return matchText(project.theme, rule as Extract<FilterRule, { type: 'text' }>);
    case 'rights':
      return matchText(project.rights, rule as Extract<FilterRule, { type: 'text' }>);
    case 'asta':
      return matchText(project.asta, rule as Extract<FilterRule, { type: 'text' }>);
    case 'ase':
      return matchText(project.ase, rule as Extract<FilterRule, { type: 'text' }>);
    case 'rin':
      return matchText(project.rin, rule as Extract<FilterRule, { type: 'text' }>);
    case 'paper_book_plan':
      return matchText(project.paper_book_plan, rule as Extract<FilterRule, { type: 'text' }>);
    case 'producer':
      return matchText(project.producers?.[0]?.name ?? null, rule as Extract<FilterRule, { type: 'text' }>);
    case 'techeditor':
      return matchText(project.techeditors?.[0]?.name ?? null, rule as Extract<FilterRule, { type: 'text' }>);
    case 'corrector':
      return matchText(project.correctors?.[0]?.name ?? null, rule as Extract<FilterRule, { type: 'text' }>);
    case 'editor':
      return matchText(project.editor_name, rule as Extract<FilterRule, { type: 'text' }>);
    case 'edition':
      return matchText(project.edition_name, rule as Extract<FilterRule, { type: 'text' }>);
    case 'department':
      return matchText(project.edition_department_code, rule as Extract<FilterRule, { type: 'text' }>);
    case 'state': {
      const r = rule as Extract<FilterRule, { type: 'select' }>;
      if (!r.values.length) return true;
      return r.values.includes(project.state ?? '');
    }
    case 'project_type': {
      const r = rule as Extract<FilterRule, { type: 'select' }>;
      if (!r.values.length) return true;
      return r.values.includes(project.project_type ?? '');
    }
    case 'review_check_type': {
      const r = rule as Extract<FilterRule, { type: 'select' }>;
      if (!r.values.length) return true;
      return r.values.includes(project.review_check_type ?? '');
    }
    case 'project_phonogram':
    case 'project_dp':
    case 'project_pdf':
    case 'project_approved':
    case 'project_done': {
      const r = rule as Extract<FilterRule, { type: 'select' }>;
      if (!r.values.length) return true;
      return r.values.includes(flagStageFilterLabel(project, columnId as FlagFilterColumn));
    }
    case 'readers': {
      const r = rule as Extract<FilterRule, { type: 'multiPerson' }>;
      const displayNames = project.readers.map((rd) => rd.name);
      if (!readerOrContractorNamesMatchText(displayNames, r)) return false;
      if (!r.names.length) return true;
      const readerNamesLower = displayNames.map((n) => n.toLowerCase());
      return r.names.some((n) => readerNamesLower.includes(n.toLowerCase()));
    }
    case 'studio': {
      const r = rule as Extract<FilterRule, { type: 'multiPerson' }>;
      const displayNames = (project.project_contracts ?? project.contractors ?? [])
        .map((c) => [contractorDisplayName(c), c.name?.trim()].filter(Boolean).join(' '))
        .filter((s) => s.trim() !== '');
      if (!readerOrContractorNamesMatchText(displayNames, r)) return false;
      if (!r.names.length) return true;
      const contractorNamesLower = displayNames.map((n) => n.toLowerCase());
      return r.names.some((n) => contractorNamesLower.includes(n.toLowerCase()));
    }
    case 'deadline':
      return matchDate(project.deadline, rule as Extract<FilterRule, { type: 'date' }>);
    case 'review_deadline':
      return matchDate(project.review_deadline, rule as Extract<FilterRule, { type: 'date' }>);
    case 'corrections_number':
      return matchNumber(project.corrections_number, rule as Extract<FilterRule, { type: 'number' }>);
    case 'pages':
      return matchNumber(project.pages, rule as Extract<FilterRule, { type: 'number' }>);
    case 'author_sheets':
      return matchNumber(project.author_sheets, rule as Extract<FilterRule, { type: 'number' }>);
    case 'recording_coef':
      return matchNumber(project.recording_coef, rule as Extract<FilterRule, { type: 'number' }>);
    case 'montage_coef':
      return matchNumber(project.montage_coef, rule as Extract<FilterRule, { type: 'number' }>);
    case 'has_music': {
      const r = rule as Extract<FilterRule, { type: 'boolean' }>;
      if (r.value === null) return true;
      const has = (project.fonoteka_track_count ?? 0) > 0;
      return has === r.value;
    }
    case 'no_edits': {
      const r = rule as Extract<FilterRule, { type: 'boolean' }>;
      if (r.value === null) return true;
      return (project.no_edits ?? false) === r.value;
    }
    default:
      return true;
  }
}
